// Import usePlayer hook
import { Volume2 } from "lucide-react";
import React from "react";
import { TranscriptSegment } from "../models/podcast";
import { usePlayer } from "./player/PlayerContext";

// Import Volume2 icon

interface TranscriptTextProps {
  segments: TranscriptSegment[];
  currentTime: number;
  showTranslation: boolean;
  onSegmentClick: (index: number) => void; // Changed to pass index
  onExplainSegment: (segment: TranscriptSegment) => void;
  // onPlaySegmentAudio is removed as TTS handles playback now
  getSegmentClasses: (segment: TranscriptSegment, index: number) => string; // Pass index to class getter
  showTimestamps?: boolean;
}

const TranscriptText: React.FC<TranscriptTextProps> = ({
  segments,
  currentTime,
  showTranslation,
  onSegmentClick,
  onExplainSegment,
  // onPlaySegmentAudio, // Removed prop
  getSegmentClasses,
  showTimestamps = true,
}) => {
  const { playSingleSegmentTTS } = usePlayer(); // Use the new function name

  // Get formatted timestamp (MM:SS)
  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className="space-y-6">
      {segments.map((segment, index) => {
        // Add index here
        // isActive logic will be handled by getSegmentClasses now
        const segmentClasses = getSegmentClasses(segment, index); // Pass index

        return (
          <div
            key={segment.id}
            className={`p-3 rounded-lg transition-colors duration-200 ${segmentClasses}`}
          >
            {/* Speaker label if available */}
            {segment.speaker && (
              <div className="flex items-center mb-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium 
                  ${segment.speaker === "Guide" ? "bg-orange-500/20 text-orange-400" : "bg-blue-500/20 text-blue-400"}`}
                >
                  {segment.speaker.charAt(0)}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-300">{segment.speaker}</span>

                {/* Timestamp if enabled */}
                {showTimestamps && (
                  <span className="ml-auto text-xs text-gray-500">
                    {formatTimestamp(segment.startTime)}
                  </span>
                )}
              </div>
            )}

            {/* Original text */}
            {/* Removed isActive class, handled by getSegmentClasses */}
            <p
              className={`text-sm cursor-pointer text-gray-400`} // Base class, active state handled by parent
              onClick={() => onSegmentClick(index)} // Pass index instead of time
            >
              {segment.text}
            </p>

            {/* Translation if enabled and available */}
            {showTranslation && segment.translation && (
              <p className="mt-1 text-sm text-orange-300 italic pl-2 border-l-2 border-orange-500/30">
                {segment.translation}
              </p>
            )}

            {/* Action buttons */}
            <div className="mt-2 flex space-x-2">
              {/* Removed the old Play button */}
              <button
                onClick={() => onExplainSegment(segment)}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 border border-orange-500/30 transition-colors"
                title="Ask AI to explain this segment"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Explain
              </button>

              {/* Add TTS Playback Button */}
              <button
                onClick={() => playSingleSegmentTTS(index)} // Call with index
                className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600/50 transition-colors"
                title="Play segment audio (TTS)"
              >
                <Volume2 size={12} />
                Speak
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TranscriptText;
